import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { trackPageView } from '@/lib/analytics';

const routes: RouteRecordRaw[] = [
  // PUBLIC — full v3 multi-page marketing surface
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('@/views/public/HomeView.vue') },
      { path: 'home', name: 'home-audience', component: () => import('@/views/public/HomeAudienceView.vue') },
      { path: 'practice', name: 'practice', component: () => import('@/views/public/PracticeView.vue') },
      { path: 'business', name: 'business', component: () => import('@/views/public/BusinessView.vue') },
      { path: 'pricing', name: 'pricing', component: () => import('@/views/public/PricingView.vue') },
      { path: 'how-it-works', name: 'how-it-works', component: () => import('@/views/public/HowItWorksView.vue') },
      { path: 'compliance', name: 'compliance', component: () => import('@/views/public/ComplianceView.vue') },
      { path: 'contact', name: 'contact', component: () => import('@/views/public/ContactView.vue') },
      { path: 'faq', name: 'faq', component: () => import('@/views/public/FaqView.vue') },
      { path: 'savings-calculator', name: 'savings-calculator', component: () => import('@/views/public/SavingsCalculatorView.vue') },
      { path: 'login', name: 'login', component: () => import('@/views/public/LoginView.vue') },
      { path: 'claim-admin', name: 'claim-admin', component: () => import('@/views/public/ClaimAdminView.vue') },

      // Plan builder — routes by ?type=home|business
      { path: 'plan', name: 'plan', component: () => import('@/views/plan-builder/PlanBuilder.vue') },

      // Subscription checkout — handoff from the plan-builder reveal or from
      // /shop confirmation (?conv=<token>&order=<id>).
      { path: 'checkout', name: 'checkout', component: () => import('@/views/checkout/SubscriptionCheckout.vue') },

      // Shop — one-off purchases
      { path: 'shop', name: 'shop', component: () => import('@/views/shop/ShopView.vue') },
      {
        path: 'shop/confirmation/:orderId',
        name: 'shop-confirmation',
        component: () => import('@/views/shop/OrderConfirmationView.vue'),
      },

      // Legacy redirect — /signup now routes to the home plan builder
      { path: 'signup', name: 'signup', redirect: { name: 'plan', query: { type: 'home' } } },
    ],
  },

  // CUSTOMER PORTAL (Phase 4+)
  {
    path: '/account',
    component: () => import('@/layouts/AccountLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'account-dashboard', component: () => import('@/views/account/DashboardView.vue') },
      { path: 'history', name: 'account-history', component: () => import('@/views/account/HistoryView.vue') },
      { path: 'documents', name: 'account-documents', component: () => import('@/views/account/DocumentsView.vue') },
      { path: 'billing', name: 'account-billing', component: () => import('@/views/account/BillingView.vue') },
      { path: 'swap', name: 'account-swap', component: () => import('@/views/account/RequestSwapView.vue') },
      { path: 'settings', name: 'account-settings', component: () => import('@/views/account/SettingsView.vue') },
      { path: 'cancel', name: 'account-cancel', component: () => import('@/views/account/CancelView.vue') },
    ],
  },

  // ADMIN (Phase 5+)
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '', name: 'admin-dashboard', component: () => import('@/views/admin/DashboardView.vue') },
      { path: 'accounts', name: 'admin-accounts', component: () => import('@/views/admin/AccountsView.vue') },
      { path: 'accounts/:id', name: 'admin-account-detail', component: () => import('@/views/admin/AccountDetailView.vue') },
      { path: 'swaps', name: 'admin-swaps', component: () => import('@/views/admin/SwapsView.vue') },
      { path: 'routes', name: 'admin-routes', component: () => import('@/views/admin/RoutesView.vue') },
      { path: 'leads', name: 'admin-leads', component: () => import('@/views/admin/LeadsView.vue') },
      { path: 'inventory', name: 'admin-inventory', component: () => import('@/views/admin/InventoryView.vue') },
      { path: 'analytics', name: 'admin-analytics', component: () => import('@/views/admin/AnalyticsView.vue') },
      { path: 'financials', name: 'admin-financials', component: () => import('@/views/admin/FinancialsView.vue') },
      { path: 'compliance', name: 'admin-compliance', component: () => import('@/views/admin/ComplianceView.vue') },
      { path: 'settings', name: 'admin-settings', component: () => import('@/views/admin/SettingsView.vue') },
    ],
  },

  // 404
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/public/NotFoundView.vue') },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, saved) {
    if (saved) return saved;
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({ el: to.hash, top: 72, behavior: 'smooth' }), 50);
      });
    }
    return { top: 0 };
  },
});

router.afterEach((to) => {
  // Auto page-view tracking — skip the entire admin section so operator
  // clicks don't pollute the marketing metrics.
  if (to.path.startsWith('/admin') || to.path.startsWith('/account')) return;
  trackPageView(to.path);
});

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true;
  const auth = useAuthStore();
  if (!auth.ready) {
    await new Promise<void>((resolve) => {
      const unwatch = auth.$subscribe(() => {
        if (auth.ready) {
          unwatch();
          resolve();
        }
      });
    });
  }
  if (!auth.isSignedIn) return { name: 'login', query: { next: to.fullPath } };
  if (to.meta.requiresAdmin && !auth.isAdmin) return { name: 'home' };
  return true;
});

export default router;
